import {
  CONNECTOR_PANELS,
  normaliseSystemName,
} from "../integrations/connectorRegistry";
import type { StudioModule } from "../services/studio/types";
import { ALL_MODULES, type ModuleDefinition } from "./registry";

/**
 * Resolution from a Studio catalogue module to the add-in tile that represents
 * it. Extracted from TaskPane once Settings → Modules needed the same mapping:
 * both surfaces must agree on which tile a backend module is, or the launcher
 * and the settings list would describe the same module differently.
 */

/**
 * Studio catalogue module names whose id doesn't equal the tile id. The add-in
 * tile ids predate the Studio module catalogue, so a couple of modules need an
 * explicit alias to resolve to their tile (AIVA-6249):
 *   - "Document Management" → the `files` tile (its File Management surface).
 *   - "Email Management"    → the `email` tile.
 * The tile *labels* now match these Studio names (see ALL_MODULES), so a direct
 * name match would resolve them too; the alias is kept as a robust, explicit
 * mapping from the Studio name to the tile id. Keyed by the module name
 * lower-cased with whitespace stripped.
 */
export const BACKEND_MODULE_TILE_ALIASES: Record<string, string> = {
  documentmanagement: "files",
  emailmanagement: "email",
};

/** Lower-case and strip whitespace — the key form used by the alias table. */
export function normaliseModuleName(name: string): string {
  return name.toLowerCase().replace(/\s+/g, "");
}

/**
 * Finds the frontend ALL_MODULES tile that should render for a backend module.
 * Tries three matches in order:
 *   1. Alias — a Studio catalogue name mapped to a tile whose id differs from
 *      it (e.g. "Document Management" → the `files` tile); see
 *      BACKEND_MODULE_TILE_ALIASES.
 *   2. Direct name match — backend "Calendar Management" → tile name
 *      "Calendar Management" (or tile id). Lets a backend module own its tile
 *      when the frontend has a dedicated entry with the same name.
 *   3. Panel-equivalence — backend "Workflow" → engage-workflow systemName
 *      maps to EngageModule → finds the engage tile so it opens the right
 *      panel even though the names diverge.
 * Returns null when no match resolves — the tile is gated out because there is
 * no panel to route the click at.
 */
export function findFrontendTileForBackendModule(
  backendModule: StudioModule,
): ModuleDefinition | null {
  const normalisedName = normaliseModuleName(backendModule.name);

  const aliasId = BACKEND_MODULE_TILE_ALIASES[normalisedName];
  if (aliasId) {
    const aliased = ALL_MODULES.find((m) => m.id === aliasId);
    if (aliased) return aliased;
  }

  const byName = ALL_MODULES.find(
    (m) =>
      m.id === normalisedName ||
      normaliseModuleName(m.name) === normalisedName,
  );
  if (byName) return byName;

  for (const connector of backendModule.connectors) {
    const panel = CONNECTOR_PANELS[normaliseSystemName(connector.systemName)];
    if (!panel) continue;
    const tile = ALL_MODULES.find((m) => CONNECTOR_PANELS[m.id] === panel);
    if (tile) return tile;
  }
  return null;
}

/**
 * Short descriptive line shown under a module's name in Settings → Modules —
 * the same wording the launcher tile carries ("PDF → Word", "Slide Design").
 *
 * Returns "" when no tile resolves, so the row degrades to just the module name
 * rather than inventing a description for a catalogue module the add-in has no
 * surface for.
 */
export function backendModuleSubtitle(backendModule: StudioModule): string {
  const tile = findFrontendTileForBackendModule(backendModule);
  if (!tile) return "";
  return tile.subtitle;
}
